import React, { useState } from "react";
import api from "@/lib/api";
import PolicyLayout from "@/components/PolicyLayout";
import { Mail, Phone, MapPin, User, FileText, Building2, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

// Company info — update these values with the actual company records.
const COMPANY = {
  legalName: "Florvanta Commerce Private Limited",
  cin: "U46419WR2026PTC293632",
  gstin: "19AAGCF9984M1ZK",
  pan: "AAGCF9984M",

  officer: {
    name: "Mr. Debasis Saibo",
    designation: "Grievance & Nodal Officer",
  },

  email: {
    support: "info@florvanta.co.in",
    grievance: "info@florvanta.co.in",
    sellers: "info@florvanta.co.in",
    privacy: "info@florvanta.co.in",
  },

  phone: {
    primary: "+91 91474 25375",
    whatsapp: "+91 91474 25375",
    hours: "Mon-Sat, 10:00 - 19:00 IST",
  },

  registered: {
    line1: "Florvanta Commerce Private Limited",
    line2: "63, Sri Arabinda Road",
    line3: "Salkia",
    city: "Howrah",
    state: "West Bengal",
    pincode: "711106",
    country: "India",
  },
};

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "Support", message: "" });
  const [sending, setSending] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — our team will get back within 1 business day.");
      setForm({ name: "", email: "", subject: "Support", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Please email info@florvanta.co.in directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <PolicyLayout
      title="Contact Us"
      tagline="We're here to help. Reach us any time — our team responds within one business day."
      updated="February 2026"
    >
      {/* Top summary cards */}
      <div className="grid md:grid-cols-3 gap-4 mb-10 not-prose">
        <div className="rounded-xl border border-[#E2E8E5] bg-white p-5" data-testid="card-email">
          <Mail className="text-[#14A085]" size={20} />
          <div className="mt-2 text-xs uppercase tracking-widest text-[#6E877C]">Email</div>
          <a href={`mailto:${COMPANY.email.support}`} className="block mt-1 font-display text-xl text-[#0F3D2E] hover:text-[#14A085]">
            {COMPANY.email.support}
          </a>
          <div className="text-sm text-[#3B5249] mt-1">General support &amp; enquiries</div>
        </div>
        <div className="rounded-xl border border-[#E2E8E5] bg-white p-5" data-testid="card-phone">
          <Phone className="text-[#14A085]" size={20} />
          <div className="mt-2 text-xs uppercase tracking-widest text-[#6E877C]">Phone</div>
          <a href={`tel:${COMPANY.phone.primary.replace(/\s/g, "")}`} className="block mt-1 font-display text-xl text-[#0F3D2E] hover:text-[#14A085]">
            {COMPANY.phone.primary}
          </a>
          <div className="text-sm text-[#3B5249] mt-1 flex items-center gap-1"><Clock size={12}/> {COMPANY.phone.hours}</div>
        </div>
        <div className="rounded-xl border border-[#E2E8E5] bg-white p-5" data-testid="card-address">
          <MapPin className="text-[#14A085]" size={20} />
          <div className="mt-2 text-xs uppercase tracking-widest text-[#6E877C]">Registered Office</div>
          <div className="mt-1 text-sm text-[#0F3D2E] leading-6">
            {COMPANY.registered.line1}<br/>
            {COMPANY.registered.line2}<br/>
            {COMPANY.registered.line3}<br/>
            {COMPANY.registered.city}, {COMPANY.registered.state} - {COMPANY.registered.pincode}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 not-prose">
        {/* Company Legal Info */}
        <div className="rounded-xl border border-[#E2E8E5] bg-white p-6" data-testid="company-info">
          <div className="flex items-center gap-2 text-[#14A085]">
            <Building2 size={18}/>
            <div className="text-xs uppercase tracking-[0.2em]">Company Information</div>
          </div>
          <h2 className="font-display text-2xl text-[#0F3D2E] mt-2">{COMPANY.legalName}</h2>

          <dl className="mt-4 divide-y divide-[#E2E8E5]">
            <Row label="GSTIN" value={COMPANY.gstin} icon={<FileText size={14}/>} tid="row-gstin" />
            <Row label="CIN" value={COMPANY.cin} tid="row-cin" />
            <Row label="PAN" value={COMPANY.pan} tid="row-pan" />
            <Row label="Grievance / Nodal Officer" value={<><b>{COMPANY.officer.name}</b><div className="text-xs text-[#6E877C]">{COMPANY.officer.designation}</div></>} icon={<User size={14}/>} tid="row-officer" />
            <Row label="Grievance Email" value={<a href={`mailto:${COMPANY.email.grievance}`} className="text-[#0F3D2E] hover:text-[#14A085]">{COMPANY.email.grievance}</a>} tid="row-grievance-email" />
            <Row label="Seller Support" value={<a href={`mailto:${COMPANY.email.sellers}`} className="text-[#0F3D2E] hover:text-[#14A085]">{COMPANY.email.sellers}</a>} tid="row-seller-email" />
            <Row label="Privacy Enquiries" value={<a href={`mailto:${COMPANY.email.privacy}`} className="text-[#0F3D2E] hover:text-[#14A085]">{COMPANY.email.privacy}</a>} tid="row-privacy-email" />
            <Row
              label="Address"
              value={<span className="text-sm">{COMPANY.registered.line1}, {COMPANY.registered.line2}, {COMPANY.registered.line3}, {COMPANY.registered.city}, {COMPANY.registered.state} - {COMPANY.registered.pincode}, {COMPANY.registered.country}</span>}
              icon={<MapPin size={14}/>}
              tid="row-address"
            />
          </dl>

          <div className="mt-5 rounded-lg bg-[#E8F5F2] p-4 text-xs text-[#0F3D2E] leading-6">
            Grievances are handled by <b>{COMPANY.officer.name}</b> under Rule 3(2) of the IT (Intermediary Guidelines) Rules, 2021 and Rule 5(3) of the Consumer Protection (E-Commerce) Rules, 2020. Acknowledgement within 48 hours, resolution within 15 days.
          </div>
        </div>

        {/* Contact form */}
        <div className="rounded-xl border border-[#E2E8E5] bg-white p-6" data-testid="contact-form-card">
          <div className="flex items-center gap-2 text-[#14A085]">
            <Send size={18}/>
            <div className="text-xs uppercase tracking-[0.2em]">Send us a message</div>
          </div>
          <h2 className="font-display text-2xl text-[#0F3D2E] mt-2">We&apos;d love to hear from you</h2>

          <form onSubmit={submit} className="mt-4 space-y-3" data-testid="contact-form">
            <div>
              <label className="text-xs text-[#3B5249]">Your name</label>
              <Input required value={form.name} onChange={upd("name")} data-testid="contact-name" />
            </div>
            <div>
              <label className="text-xs text-[#3B5249]">Your email</label>
              <Input type="email" required value={form.email} onChange={upd("email")} data-testid="contact-email" />
            </div>
            <div>
              <label className="text-xs text-[#3B5249]">Subject</label>
              <select
                value={form.subject}
                onChange={upd("subject")}
                className="mt-1 w-full h-10 rounded-md border border-[#E2E8E5] bg-white px-3 text-sm"
                data-testid="contact-subject"
              >
                <option>Support</option>
                <option>Order / Refund Issue</option>
                <option>Seller Enquiry</option>
                <option>Grievance</option>
                <option>Media / Partnership</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-[#3B5249]">Message</label>
              <Textarea rows={5} required value={form.message} onChange={upd("message")} data-testid="contact-message" />
            </div>
            <Button type="submit" disabled={sending} className="w-full rounded-full bg-[#0F3D2E] hover:bg-[#14A085] text-white" data-testid="contact-submit">
              {sending ? "Sending…" : "Send Message"}
            </Button>
            <p className="text-xs text-[#6E877C] text-center">
              Or simply email us at <a className="text-[#14A085] hover:underline" href={`mailto:${COMPANY.email.support}`}>{COMPANY.email.support}</a>
            </p>
          </form>
        </div>
      </div>

      {/* Escalation matrix */}
      <div className="mt-10 rounded-xl border border-[#E2E8E5] bg-white p-6 not-prose" data-testid="escalation-matrix">
        <h3 className="font-display text-xl text-[#0F3D2E]">Escalation Matrix</h3>
        <p className="text-sm text-[#3B5249] mt-1">If your concern is not resolved in the expected time-frame, please escalate to the next level.</p>
        <div className="overflow-auto mt-4 rounded-lg border border-[#E2E8E5]">
          <table className="w-full text-sm">
            <thead className="bg-[#E8F5F2] text-[#0F3D2E]">
              <tr>
                <th className="p-3 text-left">Level</th>
                <th className="p-3 text-left">Contact</th>
                <th className="p-3 text-left">Timeline</th>
              </tr>
            </thead>
            <tbody className="text-[#0F3D2E]">
              <tr className="border-t border-[#E2E8E5]"><td className="p-3">L1 · Customer Support</td><td className="p-3">{COMPANY.email.support}</td><td className="p-3">48 hours</td></tr>
              <tr className="border-t border-[#E2E8E5]"><td className="p-3">L2 · Grievance Officer</td><td className="p-3">{COMPANY.officer.name} — {COMPANY.email.grievance}</td><td className="p-3">Up to 15 days</td></tr>
              <tr className="border-t border-[#E2E8E5]"><td className="p-3">L3 · RBI Ombudsman / Consumer Commission</td><td className="p-3">For unresolved payment / consumer complaints</td><td className="p-3">External regulator</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </PolicyLayout>
  );
}

function Row({ label, value, icon, tid }) {
  return (
    <div className="py-3 flex items-start gap-3" data-testid={tid}>
      <div className="w-40 shrink-0 text-xs uppercase tracking-widest text-[#6E877C] flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="flex-1 text-sm text-[#0F3D2E] break-words">{value}</div>
    </div>
  );
}
